import { useState, useRef } from "react";
import type { Hospital, DebugToggles, ScenarioPricing } from "@/types/map";
import { scenarioMarkerColor } from "@/lib/grades";
import { withScenario } from "@/lib/mapNav";

interface UseMapMarkersOptions {
  viewerRef: React.MutableRefObject<any>;
  overlayContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  isDataReady: boolean;
  isOrbitingRef: React.MutableRefObject<boolean>;
  selectedHospital: Hospital | null;
  debugTogglesRef: React.MutableRefObject<DebugToggles>;
  activeScenarioId: string | null;
  scenarioHospitalPricing: ScenarioPricing;
  hospitalId: string | null;
  urlState: string | null;
  urlCity: string | null;
  isAhp: boolean;
  setSelectedHospital: (h: Hospital) => void;
  fetchHospitalProcedures: (id: string) => void;
  orbitHospital: (hospital: Hospital) => void;
  navigate: (path: string, options?: any) => void;
}

export function useMapMarkers(options: UseMapMarkersOptions) {
  const {
    viewerRef, overlayContainerRef, isDataReady, isOrbitingRef,
    selectedHospital, debugTogglesRef, activeScenarioId,
    scenarioHospitalPricing, hospitalId, urlState, urlCity, isAhp,
    setSelectedHospital, fetchHospitalProcedures, orbitHospital, navigate,
  } = options;

  const [htmlOverlays, setHtmlOverlays] = useState<Map<string, HTMLElement>>(new Map());
  // Far fewer markers on phones — 1000 pins is unreadable on a small screen and
  // slow to render. Desktop keeps the dense view.
  const [maxMarkers, setMaxMarkers] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768 ? 150 : 1000
  );
  const [markerSortOrder, setMarkerSortOrder] = useState<'cheapest' | 'expensive'>('expensive');
  const lastOverlayUpdateRef = useRef(0);
  // PERF 4e: Store camera listener cleanup to prevent leaks
  const cameraListenerCleanupRef = useRef<(() => void) | null>(null);
  // PERF 4f: Track last altitude tier to avoid unnecessary updates
  const lastAltitudeTierRef = useRef<string>('');

  const wrapEveryThirdWord = (text: string) => {
    const words = text.split(' ');
    const wrappedLines = [];
    for (let i = 0; i < words.length; i += 3) {
      wrappedLines.push(words.slice(i, i + 3).join(' '));
    }
    return wrappedLines.join('\n');
  };

  const getMarkerColor = (outlierPercentage: number, dataReady: boolean, hospital: Hospital) => {
    const scenarioPricing = scenarioHospitalPricing.get(hospital.id);
    if (activeScenarioId) {
      if (!scenarioPricing) return '#6b7280';
      return scenarioMarkerColor(scenarioPricing.blendedMarkup);
    }
    if (!hospital.followed) return '#9ca3af';
    const isIndexed = hospital.total_procedures > 0 ||
      hospital.total_chargemaster_files > 0 ||
      (hospital.outlier_percentage !== undefined && hospital.outlier_percentage > 0);
    if (!isIndexed) return '#6b7280';
    if (!dataReady) return '#6b7280';
    if (outlierPercentage >= 40) return '#dc2626';
    if (outlierPercentage >= 25) return '#ea580c';
    if (outlierPercentage >= 10) return '#eab308';
    return '#16a34a';
  };

  const getDynamicMarkerAltitude = (groundElevation: number, cameraHeight: number) => {
    const baseOffset = 50;
    if (cameraHeight > 100000) return groundElevation + 500;
    if (cameraHeight > 50000) return groundElevation + 300;
    if (cameraHeight > 20000) return groundElevation + 150;
    return groundElevation + baseOffset;
  };

  // PERF 4f: Get altitude tier string for comparison
  const getAltitudeTier = (cameraHeight: number): string => {
    if (cameraHeight > 100000) return 'far';
    if (cameraHeight > 50000) return 'medFar';
    if (cameraHeight > 20000) return 'med';
    return 'close';
  };

  const updateHtmlOverlays = (hospitals: Hospital[]) => {
    if (!debugTogglesRef.current.htmlOverlays) return;
    if (!viewerRef.current || !overlayContainerRef.current) return;

    const now = performance.now();
    if (now - lastOverlayUpdateRef.current < 200) return;
    lastOverlayUpdateRef.current = now;

    const Cesium = (window as any).Cesium;
    const viewer = viewerRef.current;
    const cameraHeight = viewer.camera.positionCartographic.height;
    const showOverlays = cameraHeight < 50000;

    if (!showOverlays) {
      htmlOverlays.forEach((overlay) => { overlay.style.display = 'none'; });
      return;
    }

    const baseOffset = 80;
    const heightFactor = Math.max(0.1, Math.min(2, 20000 / cameraHeight));
    const dynamicOffset = Math.max(40, Math.min(200, baseOffset * heightFactor));
    const scale = Math.max(0.8, Math.min(1.2, heightFactor));

    const cameraCart = viewer.camera.positionCartographic;
    const camLon = Cesium.Math.toDegrees(cameraCart.longitude);
    const camLat = Cesium.Math.toDegrees(cameraCart.latitude);
    const viewRadius = Math.max(0.5, cameraHeight / 50000);

    const screenW = viewer.scene.canvas.clientWidth;
    const screenH = viewer.scene.canvas.clientHeight;

    htmlOverlays.forEach((overlay, hospitalId) => {
      const hospital = hospitals.find(h => h.id === hospitalId);
      if (!hospital) return;

      const dLon = Math.abs(hospital.longitude - camLon);
      const dLat = Math.abs(hospital.latitude - camLat);
      if (dLon > viewRadius || dLat > viewRadius) {
        overlay.style.display = 'none';
        return;
      }

      const worldPosition = Cesium.Cartesian3.fromDegrees(hospital.longitude, hospital.latitude, 200);
      const screenPosition = Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, worldPosition);

      if (screenPosition && screenPosition.x > -200 && screenPosition.x < screenW + 200 &&
          screenPosition.y > -200 && screenPosition.y < screenH + 200) {
        overlay.style.display = 'block';
        overlay.style.left = `${screenPosition.x}px`;
        overlay.style.top = `${screenPosition.y - dynamicOffset}px`;
        overlay.style.transform = `translate(-50%, 0) scale(${scale})`;
      } else {
        overlay.style.display = 'none';
      }
    });
  };

  const addHospitalMarkers = (viewer: any, hospitals: Hospital[]) => {
    if (!hospitals.length) return;

    const Cesium = (window as any).Cesium;

    // PERF 4e: Clean up previous camera listeners before adding new ones
    if (cameraListenerCleanupRef.current) {
      cameraListenerCleanupRef.current();
      cameraListenerCleanupRef.current = null;
    }

    viewer.entities.removeAll();
    viewer.dataSources.removeAll();

    if (overlayContainerRef.current) {
      overlayContainerRef.current.innerHTML = '';
    }
    setHtmlOverlays(new Map());

    const startTime = performance.now();
    const markerCount = Math.min(hospitals.length, maxMarkers);
    const filterInfo = hospitalId ? `hospital view` : urlState ? (urlCity ? `${urlCity}, ${urlState}` : urlState) : 'USA';
    console.log(`🗺️ Creating ${markerCount} markers for ${filterInfo}`);

    // Cache SVG markers
    const markerCache = new Map<string, string>();
    const createColoredMarkerSVG = (color: string) => {
      if (markerCache.has(color)) return markerCache.get(color)!;
      const svg = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M31 16C31 24.2843 24.2843 31 16 31C7.71573 31 1 24.2843 1 16C1 7.71573 7.71573 1 16 1C24.2843 1 31 7.71573 31 16Z" stroke="${color}" stroke-width="2" fill="${color}" fill-opacity="0.2"/>
          <path d="M16 15.9999C16.3667 15.9999 16.6806 15.8694 16.9417 15.6083C17.2028 15.3471 17.3333 15.0333 17.3333 14.6666C17.3333 14.2999 17.2028 13.986 16.9417 13.7249C16.6806 13.4638 16.3667 13.3333 16 13.3333C15.6333 13.3333 15.3194 13.4638 15.0583 13.7249C14.7972 13.986 14.6667 14.2999 14.6667 14.6666C14.6667 15.0333 14.7972 15.3471 15.0583 15.6083C15.3194 15.8694 15.6333 15.9999 16 15.9999ZM16 22.6666C14.2111 21.1444 12.875 19.7305 11.9917 18.4249C11.1083 17.1194 10.6667 15.911 10.6667 14.7999C10.6667 13.1333 11.2028 11.8055 12.275 10.8166C13.3472 9.8277 14.5889 9.33325 16 9.33325C17.4111 9.33325 18.6528 9.8277 19.725 10.8166C20.7972 11.8055 21.3333 13.1333 21.3333 14.7999C21.3333 15.911 20.8917 17.1194 20.0083 18.4249C19.125 19.7305 17.7889 21.1444 16 22.6666Z" fill="${color}"/>
        </svg>
      `)}`;
      markerCache.set(color, svg);
      return svg;
    };

    // PERF 4g: Label visibility — only on moveEnd, not during movement
    const updateLabelVisibility = () => {
      const cameraHeight = viewer.camera.positionCartographic.height;
      const showLabels = cameraHeight < 75000;
      const baseOffset = -50;
      const zoomFactor = Math.max(1, Math.min(5, 50000 / cameraHeight));
      const dynamicSpacing = Math.floor(40 * zoomFactor);

      const visibleEntities = viewer.entities.values.filter((entity: any) =>
        entity.label && entity.hospital && entity.hospital.followed && showLabels && !entity.isRibbon && !entity.isStatsLabel
      );
      visibleEntities.sort((a: any, b: any) => b.hospital.latitude - a.hospital.latitude);

      const screenPositions = new Map();
      visibleEntities.forEach((entity: any) => {
        const worldPosition = entity.position.getValue(viewer.clock.currentTime);
        const screenPosition = Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, worldPosition);
        if (screenPosition) {
          const collisionDistance = dynamicSpacing * 2;
          let offsetMultiplier = 0;
          for (const [pos, count] of screenPositions.entries()) {
            const [x, y] = pos.split(',').map(Number);
            const distance = Math.sqrt(Math.pow(screenPosition.x - x, 2) + Math.pow(screenPosition.y - y, 2));
            if (distance < collisionDistance) {
              offsetMultiplier = count + 1;
              break;
            }
          }
          const cappedMultiplier = Math.min(offsetMultiplier, 3);
          const verticalOffset = baseOffset - (cappedMultiplier * 30);
          const horizontalOffset = cappedMultiplier % 2 === 0 ? 0 : (cappedMultiplier % 4 === 1 ? 60 : -60);
          entity.label.pixelOffset = new Cesium.Cartesian2(horizontalOffset, verticalOffset);
          entity.label.show = true;

          const posKey = `${Math.round(screenPosition.x / dynamicSpacing) * dynamicSpacing},${Math.round(screenPosition.y / dynamicSpacing) * dynamicSpacing}`;
          screenPositions.set(posKey, (screenPositions.get(posKey) || 0) + 1);
        }
      });

      viewer.entities.values.forEach((entity: any) => {
        if (entity.label && entity.hospital) {
          if (entity.isStatsLabel) {
            entity.label.show = showLabels &&
              entity.hospital.followed &&
              selectedHospital?.id === entity.hospital.id &&
              isOrbitingRef.current;
          } else if (!(showLabels && entity.hospital.followed)) {
            entity.label.show = false;
          }
        }
      });
    };

    const getCameraHeight = () => {
      if (!viewer.camera) return 10000;
      return Cesium.Cartographic.fromCartesian(viewer.camera.position).height;
    };

    const initialCameraHeight = getCameraHeight();

    // Sort and limit hospitals
    const sortedHospitals = [...hospitals].sort((a, b) => {
      if (markerSortOrder === 'expensive') {
        return (b.outlier_percentage || 0) - (a.outlier_percentage || 0);
      }
      return (a.outlier_percentage || 0) - (b.outlier_percentage || 0);
    });
    const limitedHospitals = sortedHospitals.slice(0, maxMarkers);

    // PERF 4h: Lazy stats label — single reusable entity instead of 1000
    let statsLabelEntity: any = null;

    // PERF 4i: Merge polylines into main entity
    limitedHospitals.forEach((hospital) => {
      const groundElevation = (hospital as any).elevation || 0;
      const markerAltitude = getDynamicMarkerAltitude(groundElevation, initialCameraHeight);
      const markerColor = getMarkerColor(hospital.outlier_percentage || 0, isDataReady, hospital);

      const scenarioPricingForHospital = scenarioHospitalPricing.get(hospital.id);
      const showLabel = activeScenarioId ? !!scenarioPricingForHospital : hospital.followed;

      // Create unified marker entity with billboard + label + polyline
      viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(hospital.longitude, hospital.latitude, markerAltitude),
        billboard: {
          image: createColoredMarkerSVG(markerColor),
          scale: 1.2,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, 0),
          scaleByDistance: new Cesium.NearFarScalar(2000, 1.5, 50000, 0.8),
        },
        label: {
          text: wrapEveryThirdWord(hospital.name),
          font: '28pt Inter, Arial, sans-serif',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -50),
          scaleByDistance: new Cesium.NearFarScalar(1000, 1.2, 10000, 0.5),
          eyeOffset: new Cesium.Cartesian3(0, 0, -10),
          show: showLabel,
          backgroundColor: markerColor ? Cesium.Color.fromCssColorString(markerColor).withAlpha(0.8) : Cesium.Color.BLACK.withAlpha(0.8),
          backgroundPadding: new Cesium.Cartesian2(12, 8),
          showBackground: true,
        },
        // PERF 4i: Attach polyline directly to billboard entity
        polyline: {
          positions: Cesium.Cartesian3.fromDegreesArrayHeights([
            hospital.longitude, hospital.latitude, groundElevation,
            hospital.longitude, hospital.latitude, markerAltitude
          ]),
          width: 4,
          material: Cesium.Color.fromCssColorString(markerColor),
          arcType: Cesium.ArcType.NONE,
        },
        hospital: hospital,
      });
    });

    // PERF 4h: Create single reusable stats label
    statsLabelEntity = viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(0, 0, 0),
      label: {
        text: '',
        font: 'bold 24pt Inter, Arial, sans-serif',
        fillColor: Cesium.Color.YELLOW,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -140),
        eyeOffset: new Cesium.Cartesian3(0, 0, -5),
        show: false,
        backgroundColor: Cesium.Color.BLACK.withAlpha(0.7),
        backgroundPadding: new Cesium.Cartesian2(10, 10),
        showBackground: true,
      },
      isStatsLabel: true,
    });

    const endTime = performance.now();
    console.log(`✅ Created ${limitedHospitals.length} markers in ${(endTime - startTime).toFixed(0)}ms (entities: ${viewer.entities.values.length})`);

    // PERF 4f: Throttled altitude updates — only when tier changes
    const updateMarkerAltitudes = () => {
      const currentCameraHeight = getCameraHeight();
      const currentTier = getAltitudeTier(currentCameraHeight);

      // Skip if tier hasn't changed
      if (currentTier === lastAltitudeTierRef.current) return;
      lastAltitudeTierRef.current = currentTier;

      viewer.entities.values.forEach((entity: any) => {
        if (entity.hospital && entity.position) {
          const hospital = entity.hospital;
          const groundElevation = (hospital as any).elevation || 0;
          const newMarkerAltitude = getDynamicMarkerAltitude(groundElevation, currentCameraHeight);

          entity.position = Cesium.Cartesian3.fromDegrees(
            hospital.longitude, hospital.latitude, newMarkerAltitude
          );

          if (entity.polyline) {
            entity.polyline.positions = Cesium.Cartesian3.fromDegreesArrayHeights([
              hospital.longitude, hospital.latitude, groundElevation,
              hospital.longitude, hospital.latitude, newMarkerAltitude
            ]);
          }
        }
      });
    };

    // PERF 4g: Label visibility only on moveEnd (not changed)
    viewer.camera.moveEnd.addEventListener(updateLabelVisibility);
    // PERF 4f: Altitude updates on changed but throttled by tier
    viewer.camera.changed.addEventListener(updateMarkerAltitudes);
    viewer.camera.moveEnd.addEventListener(updateMarkerAltitudes);

    // HTML overlays
    const onMoveEndOverlays = () => updateHtmlOverlays(hospitals);
    viewer.camera.moveEnd.addEventListener(onMoveEndOverlays);

    // Initial updates
    updateLabelVisibility();
    setTimeout(() => updateHtmlOverlays(hospitals), 100);

    // Handle marker clicks
    const onSelectedEntityChanged = () => {
      const selectedEntity = viewer.selectedEntity;
      if (selectedEntity && selectedEntity.hospital) {
        const hospital = selectedEntity.hospital;
        setSelectedHospital(hospital);
        fetchHospitalProcedures(hospital.id);
        orbitHospital(hospital);
        navigate(withScenario(`/map/${hospital.state.toLowerCase()}/${hospital.id}`), { replace: true });
      }
    };
    viewer.selectedEntityChanged.addEventListener(onSelectedEntityChanged);

    // Update stats label for selected hospital during orbit
    const updateStatsLabel = () => {
      if (!statsLabelEntity) return;
      if (selectedHospital && isOrbitingRef.current) {
        const ranking = (selectedHospital as any).overall_rank_gross || '?';
        const totalHospitals = hospitals.filter(h => h.followed).length;
        const outlierPercentage = (selectedHospital.outlier_percentage || 0).toFixed(1);
        const scenarioPricingForHospital = scenarioHospitalPricing.get(selectedHospital.id);

        const statsText = activeScenarioId && scenarioPricingForHospital
          ? `$${scenarioPricingForHospital.blendedPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}  |  ${scenarioPricingForHospital.blendedMarkup > 0 ? '+' : ''}${scenarioPricingForHospital.blendedMarkup.toFixed(0)}% markup`
          : `#${ranking}/${totalHospitals}  |  ${outlierPercentage}% Outliers`;

        const groundElevation = (selectedHospital as any).elevation || 0;
        const markerAltitude = getDynamicMarkerAltitude(groundElevation, getCameraHeight());

        statsLabelEntity.position = Cesium.Cartesian3.fromDegrees(
          selectedHospital.longitude, selectedHospital.latitude, markerAltitude
        );
        statsLabelEntity.label.text = statsText;
        statsLabelEntity.label.show = true;
        statsLabelEntity.hospital = selectedHospital;
      } else {
        statsLabelEntity.label.show = false;
      }
    };

    // Run stats update periodically during orbit
    viewer.clock.onTick.addEventListener(updateStatsLabel);

    // PERF 4e: Store cleanup for ALL viewer listeners added above (camera,
    // selection, clock) so re-adding markers doesn't stack duplicates/leaks.
    cameraListenerCleanupRef.current = () => {
      try {
        viewer.camera.moveEnd.removeEventListener(updateLabelVisibility);
        viewer.camera.changed.removeEventListener(updateMarkerAltitudes);
        viewer.camera.moveEnd.removeEventListener(updateMarkerAltitudes);
        viewer.camera.moveEnd.removeEventListener(onMoveEndOverlays);
        viewer.selectedEntityChanged.removeEventListener(onSelectedEntityChanged);
        viewer.clock.onTick.removeEventListener(updateStatsLabel);
      } catch (e) {
        // Viewer may already be destroyed
      }
    };
  };

  return {
    htmlOverlays,
    maxMarkers,
    setMaxMarkers,
    markerSortOrder,
    setMarkerSortOrder,
    addHospitalMarkers,
  };
}
